import {ReactElement, useState} from "react";
import {
  LAB_TEST_TEMPLATE_PARAMETER_UNIT_KTS,
  LabTestResult,
  LabTestResultStatus,
  LabTestTemplateParametersFreeform,
  LabTestTemplateParametersMulti,
  LabTestTemplateParametersNested,
  LabTestTemplateParametersSingle, LabTestTemplateType
} from "../types";
import {Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography} from "@mui/material";
import {DIVIDER_BLACK, DIVIDER_XBLACK, PD_MD, SZ_JUMBO, SZ_XXLG} from "@/shared/dimens";
import {colorHighlightAlt, colorRed, lightGray} from "@/shared/colors";
import {StyledHorizontalDivider, StyledMarkdown} from "@/shared/StyledComponents";

export enum LabTestResultViewMode {
  TEMPLATE = "template",
  READ = "read",
  CREATE = "create",
  EDIT = "edit",
}

// export enum LabTestTemplateType {
//   SINGLE = "single",
//   MULTI = "multi",
//   NESTED = "nested",
//   FREEFORM = "freeform",
// }

function focusPrev(): void {
  const tabIndex = ((document.activeElement as HTMLElement)?.tabIndex || 0) - 1;
  // console.log((document.activeElement as HTMLElement)?.tabIndex + ", " + tabIndex);
  const target = document.querySelectorAll(`[tabindex="${tabIndex + 1}"]`)?.[0] as HTMLElement;
  target?.focus();
}

function focusNext(): void {
  const tabIndex = ((document.activeElement as HTMLElement)?.tabIndex || 0) + 1;
  // console.log((document.activeElement as HTMLElement)?.tabIndex + ", " + tabIndex);
  const target = document.querySelectorAll(`[tabindex="${tabIndex}"]`)?.[0] as HTMLElement;
  target?.focus();
}

function renderRows(result: LabTestResult, mode: LabTestResultViewMode): ReactElement {
  const labTest = result.labTest;
  const template = result.overrideTemplate || labTest.templates?.[result.labTestTemplateIndex || 0];
  if (template) {
    const parameters = template.getParameters();
    switch (parameters.paramsType) {
      case LabTestTemplateType.SINGLE:
        return renderRowsSingle(result.values, labTest.name, parameters as LabTestTemplateParametersSingle, mode, result.status);
      case LabTestTemplateType.MULTI:
        return renderRowsMulti(result.values, labTest.name, parameters as LabTestTemplateParametersMulti, mode, result.status);
      case LabTestTemplateType.NESTED:
        return renderRowsNested(result.values, labTest.name, parameters as LabTestTemplateParametersNested, mode, result.status);
      case LabTestTemplateType.FREEFORM:
        return renderRowsFreeform(result.values, labTest.name, parameters as LabTestTemplateParametersFreeform, mode, result.status);
    }
  }
  return renderRowsError(`No template found for: ${labTest.name}`);
}

function renderRowsError(error: string): ReactElement {
  return <>
    <TableRow sx={{borderTop: DIVIDER_XBLACK}}>
      <TableCell colSpan={4} sx={{color: colorRed}}>{error}</TableCell>
    </TableRow>
  </>;
}

function onKeyDown(event): void {
  switch (event.key) {
    case "Enter":
    case "ArrowDown":
      event.preventDefault();
      focusNext();
      break;
    case "ArrowUp":
      event.preventDefault();
      focusPrev();
      break;
  }
}

function TextFieldValue({values, slug, status}: {
  values: any,
  slug: string,
  status: LabTestResultStatus
}): ReactElement {
  const [value, setValue] = useState<string>(values?.[slug]);
  return <TextField
    disabled={Boolean(status) && status !== LabTestResultStatus.INCOMPLETE && status !== LabTestResultStatus.COMPLETED}
    size={"small"}
    value={value}
    onChange={e => setValue(e.target.value)}
    onBlur={e => {
      values[slug] = value;
    }}
    onKeyDown={event => {
      // TODO Doesn't quite work well yet.
      // onKeyDown(event);
    }}/>;
}

function renderValue(values: any, slug: string, defaultValue: string, mode: LabTestResultViewMode, status: LabTestResultStatus): ReactElement | string {
  switch (mode) {
    case LabTestResultViewMode.CREATE:
    case LabTestResultViewMode.EDIT:
      return <TextFieldValue values={values} slug={slug} status={status}/>;
    case LabTestResultViewMode.READ:
      return "-";
    case LabTestResultViewMode.TEMPLATE:
      return defaultValue;
  }
}

function renderRowsSingle(values: any, name: string, templateParameters: LabTestTemplateParametersSingle, mode: LabTestResultViewMode, status: LabTestResultStatus): ReactElement {
  if (templateParameters?.paramsType !== LabTestTemplateType.SINGLE) {
    return null;
  }
  return <>
    <TableRow sx={{
      borderTop: DIVIDER_XBLACK,
      '&:focus-within': {
        backgroundColor: colorHighlightAlt,
      },
    }}>
      <TableCell style={{fontWeight: "bold"}}>{name.toUpperCase()}</TableCell>
      <TableCell>{renderValue(values, LabTestTemplateParametersSingle.DEFAULT_SLUG, templateParameters.defaultValue, mode, status)}</TableCell>
      <TableCell>{LAB_TEST_TEMPLATE_PARAMETER_UNIT_KTS.findText(templateParameters.unit?.toLowerCase())}</TableCell>
      <TableCell>{templateParameters.reference}</TableCell>
    </TableRow>
  </>;
}

function renderRowsMulti(values: any, name: string, templateParameters: LabTestTemplateParametersMulti, mode: LabTestResultViewMode, status: LabTestResultStatus): ReactElement {
  if (templateParameters?.paramsType !== LabTestTemplateType.MULTI) {
    return null;
  }
  return <>
    <TableRow sx={{borderTop: DIVIDER_XBLACK}}>
      <TableCell style={{fontWeight: "bold"}} colSpan={4}>{name.toUpperCase()}</TableCell>
    </TableRow>
    {templateParameters.items.map(item =>
      <TableRow sx={{
        '&:focus-within': {
          backgroundColor: colorHighlightAlt,
        },
      }}>
        <TableCell style={{paddingLeft: PD_MD}}>{item.name}</TableCell>
        <TableCell>{renderValue(values, item.slug, item.value.defaultValue, mode, status)}</TableCell>
        <TableCell>{LAB_TEST_TEMPLATE_PARAMETER_UNIT_KTS.findText(item.value?.unit?.toLowerCase())}</TableCell>
        <TableCell>{item.value.reference}</TableCell>
      </TableRow>)}
  </>;
}

function renderRowsNested(values: any, name: string, templateParameters: LabTestTemplateParametersNested, mode: LabTestResultViewMode, status: LabTestResultStatus): ReactElement {
  if (templateParameters?.paramsType !== LabTestTemplateType.NESTED) {
    return null;
  }
  return <>
    <TableRow sx={{borderTop: DIVIDER_XBLACK}}>
      <TableCell style={{fontWeight: "bold"}} colSpan={4}>{name.toUpperCase()}</TableCell>
    </TableRow>
    {templateParameters.groups.map(group => <>
      <TableRow sx={{borderTop: DIVIDER_BLACK}}>
        <TableCell colSpan={4} style={{paddingLeft: PD_MD}}>
          {group.name?.toUpperCase()}
        </TableCell>
      </TableRow>
      {group.items.map(item =>
        <TableRow sx={{
          '&:focus-within': {
            backgroundColor: colorHighlightAlt,
          },
        }}>
          <TableCell style={{paddingLeft: PD_MD + PD_MD}}>{item.name}</TableCell>
          <TableCell>{renderValue(values, item.slug, item.value?.defaultValue, mode, status)}</TableCell>
          <TableCell>{LAB_TEST_TEMPLATE_PARAMETER_UNIT_KTS.findText(item.value?.unit?.toLowerCase())}</TableCell>
          <TableCell>{item.value?.reference}</TableCell>
        </TableRow>)}
    </>)}
  </>;
}

function renderRowsFreeform(value: any, name: string, templateParameters: LabTestTemplateParametersFreeform, mode: LabTestResultViewMode, status: LabTestResultStatus): ReactElement {
  if (templateParameters?.paramsType !== LabTestTemplateType.FREEFORM) {
    return null;
  }
  return <>
    <TableRow sx={{borderTop: DIVIDER_XBLACK}}>
      <TableCell style={{fontWeight: "bold"}} colSpan={4}>{name.toUpperCase()}</TableCell>
    </TableRow>
    <TableRow>
      <TableCell colSpan={4}><StyledMarkdown>{templateParameters.content}</StyledMarkdown></TableCell>
    </TableRow>
  </>;
}

const _labTestResultViewRemarkPlugins: any[] = [];

export function registerLabTestResultViewRemarkPlugins(plugin: any): void {
  _labTestResultViewRemarkPlugins.push(plugin);
}

export function LabTestResultView({result, mode}: {
  result: LabTestResult,
  mode?: LabTestResultViewMode,
}): ReactElement {
  if (!result) {
    return null;
  }
  const labTest = result.labTest;
  const template = result.overrideTemplate || labTest.templates?.[result.labTestTemplateIndex || 0];
  return <div style={{display: "flex", flexDirection: "column"}}>
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow style={{background: lightGray}}>
            <TableCell style={{flexBasis: 0, width: SZ_JUMBO, flexGrow: 1}}><b>Name</b></TableCell>
            <TableCell style={{width: SZ_JUMBO, maxWidth: "20%"}}><b>Value</b></TableCell>
            <TableCell style={{width: SZ_XXLG, maxWidth: "10%"}}><b>Unit</b></TableCell>
            <TableCell style={{width: SZ_XXLG, maxWidth: "10%"}}><b>Reference</b></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {renderRows(result, mode || LabTestResultViewMode.READ)}
        </TableBody>
      </Table>
    </TableContainer>
    <StyledHorizontalDivider/>
    {Boolean(template.notes)
      ? <div style={{marginTop: PD_MD, display: "flex", flexDirection: "column", padding: PD_MD}}>
        <Typography variant={"h6"}>Notes / Interpretation</Typography>
        <StyledMarkdown remarkPlugins={_labTestResultViewRemarkPlugins}>
          {template.notes}
        </StyledMarkdown>
      </div>
      : null}
  </div>;
}